import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let platform = 'unknown';
  let browser = 'unknown';
  if (/iPhone|iPad|iPod/.test(ua)) platform = 'ios';
  else if (/Android/.test(ua)) platform = 'android';
  else if (/Mac/.test(ua)) platform = 'macos';
  else if (/Win/.test(ua)) platform = 'windows';
  else if (/Linux/.test(ua)) platform = 'linux';

  if (/Chrome/.test(ua) && !/Edg/.test(ua)) browser = 'chrome';
  else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = 'safari';
  else if (/Firefox/.test(ua)) browser = 'firefox';
  else if (/Edg/.test(ua)) browser = 'edge';

  return { platform, browser, device_name: `${platform}-${browser}` };
}

export function useIsStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

export function useIsIOS() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const isStandalone = useIsStandalone();
  const isIOS = useIsIOS();
  const needsIOSInstall = isIOS && !isStandalone;

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && !!VAPID_PUBLIC_KEY;
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
      navigator.serviceWorker.ready.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          setIsSubscribed(!!sub);
        });
      });
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!isSupported) return false;
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }

      await api.post('/notifications/push/subscribe', {
        subscription: sub.toJSON(),
        ...getDeviceInfo(),
      });

      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error('[Push] Subscribe failed:', err);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await api.post('/notifications/push/unsubscribe', { endpoint });
      }
      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.error('[Push] Unsubscribe failed:', err);
      return false;
    }
  }, []);

  const testPush = useCallback(async () => {
    try {
      await api.post('/notifications/push/test');
      return true;
    } catch {
      return false;
    }
  }, []);

  return { permission, isSubscribed, isSupported, subscribe, unsubscribe, testPush, needsIOSInstall, isIOS, isStandalone };
}
